import { useEffect } from "react";

export interface SEOProps {
    title?: string;
    description?: string;
    keywords?: string;
    ogImage?: string;
    ogType?: "website" | "profile" | "article";
    canonicalUrl?: string;
    structuredData?: Record<string, any>;
}

/**
 * SEO Component - Manages dynamic meta tags for each page
 * Updates document.title and meta tags in the document head
 */
export function SEO({
    title,
    description,
    keywords,
    ogImage,
    ogType = "website",
    canonicalUrl,
    structuredData,
}: SEOProps) {
    useEffect(() => {
        // Update document title
        if (title) {
            document.title = title;
        }

        // Helper function to update or create meta tag
        const updateMetaTag = (
            selector: string,
            content: string,
            attribute: "name" | "property" = "name"
        ) => {
            let element = document.querySelector(selector) as HTMLMetaElement;
            if (!element) {
                element = document.createElement("meta");
                element.setAttribute(attribute, selector.replace(/meta\[(name|property)="(.+)"\]/, "$2"));
                document.head.appendChild(element);
            }
            element.content = content;
        };

        // Update basic meta tags
        if (description) {
            updateMetaTag('meta[name="description"]', description);
        }

        if (keywords) {
            updateMetaTag('meta[name="keywords"]', keywords);
        }

        // Update Open Graph tags
        if (title) {
            updateMetaTag('meta[property="og:title"]', title, "property");
        }

        if (description) {
            updateMetaTag('meta[property="og:description"]', description, "property");
        }

        if (ogImage) {
            updateMetaTag('meta[property="og:image"]', ogImage, "property");
        }

        if (ogType) {
            updateMetaTag('meta[property="og:type"]', ogType, "property");
        }

        if (canonicalUrl) {
            updateMetaTag('meta[property="og:url"]', canonicalUrl, "property");
        }

        // Update Twitter Card tags
        if (title) {
            updateMetaTag('meta[name="twitter:title"]', title);
        }

        if (description) {
            updateMetaTag('meta[name="twitter:description"]', description);
        }

        if (ogImage) {
            updateMetaTag('meta[name="twitter:image"]', ogImage);
        }

        // Update canonical URL
        if (canonicalUrl) {
            let linkElement = document.querySelector('link[rel="canonical"]') as HTMLLinkElement;
            if (!linkElement) {
                linkElement = document.createElement("link");
                linkElement.rel = "canonical";
                document.head.appendChild(linkElement);
            }
            linkElement.href = canonicalUrl;
        }

        // Update structured data
        if (structuredData) {
            const scriptId = "dynamic-structured-data";
            let scriptElement = document.getElementById(scriptId) as HTMLScriptElement;

            if (!scriptElement) {
                scriptElement = document.createElement("script");
                scriptElement.id = scriptId;
                scriptElement.type = "application/ld+json";
                document.head.appendChild(scriptElement);
            }

            scriptElement.textContent = JSON.stringify(structuredData);
        }
    }, [title, description, keywords, ogImage, ogType, canonicalUrl, structuredData]);

    // This component doesn't render anything
    return null;
}
